/**
 * Returns true when a JWT's `exp` claim is in the past — or when the
 * token is missing, malformed, or unparseable. We deliberately treat
 * "can't decide" as "expired" so callers err on the side of throwing
 * the token away rather than sending it.
 *
 * No signature verification — that's the server's job. We only read
 * the `exp` claim to decide whether attaching this token would be a
 * waste of a round-trip.
 */
export function isJwtExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  const parts = token.split(".");
  if (parts.length !== 3) return true;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as { exp?: unknown };
    if (typeof payload.exp !== "number") return true;
    // exp is seconds since epoch. Treat anything within the next 5s as
    // already expired — the request would arrive at the server stale,
    // and a small client/server clock skew shouldn't cause us to ship a
    // token the server is about to reject.
    const SKEW_MS = 5_000;
    return payload.exp * 1000 <= Date.now() + SKEW_MS;
  } catch {
    return true;
  }
}

function base64UrlDecode(s: string): string {
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad);
  return atob(b64);
}
